import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import '../../../../css/CustomComponents/CustomSelect.css';
import Pulse from '../../../Loader/Pulse';
import { ArrowDown2 } from '../../../CustomComponents/SwiftIcon/Icons';
import { COLOR_VARS } from '../../../../exports/ColorVars';

/**
 * Portal version of CustomSelect.
 *
 * Same props as CustomSelect:
 *   options, defaultIndex, value, title, placeholder,
 *   onTypeChange, width, height, heading, disabled, icon
 *
 * The dropdown list is rendered via createPortal so it escapes
 * overflow:hidden / overflow:scroll containers (tables, popups, etc.).
 *
 * Usage — just swap the import:
 *   import CustomSelect from '.../CustomSelect/CustomSelectPortal';
 */
const CustomSelectPortal = ({
  options = [],
  defaultIndex,
  value: controlledValue,
  title: defaultTitle,
  placeholder,
  onTypeChange,
  width,
  height,
  heading,
  disabled,
  icon,
}) => {
  // ───────── State ─────────
  const [isListOpen, setIsListOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex ?? null);
  const [displayTitle, setDisplayTitle] = useState(
    options[defaultIndex] || defaultTitle || ''
  );
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);

  const headerRef = useRef(null);

  // ───────── Sync props → state ─────────
  useEffect(() => {
    setDisplayTitle(options[defaultIndex] || defaultTitle || '');
    setSelectedIndex(defaultIndex ?? null);
  }, [defaultIndex, options, defaultTitle]);

  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== displayTitle) {
      const idx = options.indexOf(controlledValue);
      setDisplayTitle(controlledValue);
      setSelectedIndex(idx > -1 ? idx : null);
    }
  }, [controlledValue, options]);

  // ───────── Position the portal dropdown ─────────
  const updatePosition = useCallback(() => {
    if (headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Use useLayoutEffect so coords are set synchronously before paint
  useLayoutEffect(() => {
    if (isListOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    } else {
      setCoords(null);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isListOpen, updatePosition]);

  // ───────── Close on outside click ─────────
  useEffect(() => {
    if (!isListOpen) return;

    const handleClick = (e) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(e.target) &&
        !e.target.closest('.custom-select-portal-dropdown')
      ) {
        setIsListOpen(false);
      }
    };

    // Defer so the opening click doesn't immediately close
    const id = setTimeout(() => {
      window.addEventListener('click', handleClick);
    }, 0);

    return () => {
      clearTimeout(id);
      window.removeEventListener('click', handleClick);
    };
  }, [isListOpen]);

  // ───────── Handlers ─────────
  const toggleSelect = (e) => {
    if (!disabled) setIsListOpen((prev) => !prev);
  };

  const selectOption = (indx, option) => {
    setIsListOpen(false);

    const optionValue = option.value || option.label || option;
    const optionLabel = option.label || option;

    const loaderTimer = setTimeout(() => {
      setLoading(true);
    }, 10000);

    Promise.resolve(onTypeChange(optionValue)).then(() => {
      clearTimeout(loaderTimer);
      if (loading) {
        setTimeout(() => {
          setDisplayTitle(optionLabel);
          setSelectedIndex(indx);
          setLoading(false);
        }, 500);
      } else {
        setDisplayTitle(optionLabel);
        setSelectedIndex(indx);
        setLoading(false);
      }
    });
  };

  // ───────── Render ─────────
  const shownTitle = loading ? <Pulse /> : displayTitle || placeholder;

  // Only render portal when coords are ready
  const showPortal = !disabled && isListOpen && coords;

  return (
    <div className={`custom-select ${disabled ? 'custom-select-disabled' : ''}`}>
      {heading && <p className="select-title">{heading}</p>}

      {/* Header / trigger */}
      <div
        ref={headerRef}
        className={`custom-select-header ${isListOpen ? 'open' : ''}`}
        tabIndex={disabled ? -1 : 0}
        onClick={toggleSelect}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          outline: 'none',
        }}
      >
        {icon && (
          <span className="custom_select_icon">
            <ArrowDown2 size={14} color={COLOR_VARS['SWIFT_COLOR4']} />
          </span>
        )}

        <span className="custom-select-title">{shownTitle}</span>

        <span
          className={`custom_select_icon ${
            isListOpen ? 'rotate-clockwise' : 'rotate-anticlockwise'
          }`}
        >
          <ArrowDown2 size={14} color={COLOR_VARS['SWIFT_COLOR4']} />
        </span>
      </div>

      {/* Portal dropdown — opens ABOVE the trigger */}
      {showPortal &&
        createPortal(
          <div
            className="custom-select-portal-dropdown"
            style={{
              position: 'fixed',
              top: `${coords.top + 4}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              zIndex: 2147483647,
              padding: '10px 8px',
              background: 'var(--glass-bg, var(--white, #fff))',
              border: '1px solid var(--glass-border, var(--border-color, #e0e0e0))',
              boxShadow: '0px -2px 12px 2px var(--primary10, rgba(0,0,0,0.08))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: '8px',
              overflowY: 'auto',
              maxHeight: '200px',
              margin: 0,
            }}
          >
            {options.map((option, indx) => {
              const optLabel = option.label || option;
              const isDisabled = option.disabled;

              return (
                <div
                  key={indx}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    transition: 'color 300ms ease',
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      selectOption(indx, option);
                    }
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '10px 3px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      borderBottom: '1px solid var(--border-color, #e0e0e0)',
                      width: '100%',
                      opacity: isDisabled ? 0.5 : 1,
                      color: selectedIndex === indx ? 'var(--font-primary)' : 'inherit',
                      backgroundColor: selectedIndex === indx ? 'var(--primary10, rgba(0,95,255,0.06))' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.backgroundColor = 'var(--primary10, rgba(0,95,255,0.06))';
                        e.currentTarget.style.color = 'var(--primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.backgroundColor = selectedIndex === indx ? 'var(--primary10, rgba(0,95,255,0.06))' : 'transparent';
                        e.currentTarget.style.color = selectedIndex === indx ? 'var(--font-primary)' : 'inherit';
                      }
                    }}
                  >
                    {optLabel}
                  </span>
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomSelectPortal;
